import * as THREE from 'three';

const equatorialRadius = 6.378137e6;
const flatteningFactor = 3.35281066474748e-3;
const eccentricitySquared = flatteningFactor * (2 - flatteningFactor);

export type Place = {
  lat: number;
  long: number;
  alt: number;
};

/*
  Function:
    Convert longitude, latitude, and altitude
    to XYZ position in the ECEF coordinate system.
  Context & Use:
    The Google API uses the ECEF system for tile positioning
    and rotation (relative to the ECEF axes).
*/
export const locationToEcef = (loc: Place): THREE.Vector3 => {
  const latRad = loc.lat * (Math.PI / 180);
  const longRad = loc.long * (Math.PI / 180);

  const N =
    equatorialRadius /
    Math.sqrt(1 - eccentricitySquared * Math.sin(latRad) * Math.sin(latRad));

  const x = (N + loc.alt) * Math.cos(latRad) * Math.cos(longRad);
  const y = (N + loc.alt) * Math.cos(latRad) * Math.sin(longRad);
  const z = (N * (1 - eccentricitySquared) + loc.alt) * Math.sin(latRad);

  return new THREE.Vector3(x, y, z);
};

/*
  Function:
    Construct a bounding sphere in the ECEF
    system from longitude, latitude, and altitude.
  Context & Use:
    We use a bounding sphere to check
    for intersections with tile bounding boxes.
    This allows us retrieve all tiles from the area
    bounded by this sphere.
*/
export const locationToArea = (loc: Place, radius: number): THREE.Sphere => {
  const center = locationToEcef(loc);
  return new THREE.Sphere(center, radius);
};

/*
  Function:
    Calculates the surface plane rotation
    relative to the ECEF's up axis.
  Context & Use:
    Tile geometries aren't facing upward by default.
    We need to apply the counter rotation,
    calculated based on the surface rotation,
    so that they lie flat in our scene.
*/
export const locationToSurfaceRotation = (loc: Place): THREE.Quaternion => {
  const ecef = locationToEcef(loc);
  const up = new THREE.Vector3(ecef.x, ecef.z, -ecef.y).normalize();
  const targetUp = new THREE.Vector3(0, 1, 0);
  return new THREE.Quaternion().setFromUnitVectors(up, targetUp);
};
